import { isCreatableGitBranchName } from "@arti/shared-source-control/gitBranchName";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../common/Button";
import { Modal } from "../../common/Modal";
import { SearchableOptionList } from "../../common/SearchableOptionList";
import {
  type AttachRepositoryInput,
  RepositoryAttachError,
  type SourceControlBranchOption,
  type SourceControlRepositoryOption,
  type WorkspaceRepositoryPermission,
  useSourceControlCatalog,
} from "../useChannelWorkspaceRepositories";
import { AttachRepositoryBatchModal } from "./AttachRepositoryBatchModal";
import { ModalErrorBlock } from "./ConfirmModal";

type BranchMode = "create" | "existing";
type CollisionPolicy = "fail" | "reuse_existing" | "suffix";

const FIELD_LABEL_CLASS =
  "grid gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500";
const FIELD_INPUT_CLASS =
  "border border-slate-300 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";
const LINK_BUTTON_CLASS =
  "cursor-pointer justify-self-start font-mono text-[10px] uppercase tracking-[0.18em] text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-200";

interface AttachRepoModalProps {
  attachedRepoKeys: ReadonlySet<string>;
  busy: boolean;
  errorCause?: unknown;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (input: AttachRepositoryInput) => void;
  onAttach: (input: AttachRepositoryInput) => Promise<void>;
  progressMessage?: string | null;
}

export function AttachRepoModal(props: AttachRepoModalProps) {
  const [batchMode, setBatchMode] = useState(true);
  return batchMode ? (
    <AttachRepositoryBatchModal
      attachedRepoKeys={props.attachedRepoKeys}
      onAttach={props.onAttach}
      onClose={props.onClose}
      onSingle={() => setBatchMode(false)}
    />
  ) : (
    <SingleRepoModal {...props} onBatch={() => setBatchMode(true)} />
  );
}

function SingleRepoModal({
  attachedRepoKeys,
  busy,
  errorCause = null,
  errorMessage,
  onClose,
  onSubmit,
  progressMessage = null,
  onBatch,
}: AttachRepoModalProps & { onBatch: () => void }) {
  const catalog = useSourceControlCatalog(true);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [repositoryId, setRepositoryId] = useState<string | null>(null);
  const [permission, setPermission] =
    useState<WorkspaceRepositoryPermission>("read");
  const [branch, setBranch] = useState("");
  const branchTouchedRef = useRef(false);
  const [repositories, setRepositories] = useState<
    SourceControlRepositoryOption[]
  >([]);
  const [repositoriesLoading, setRepositoriesLoading] = useState(false);
  const [repositoriesError, setRepositoriesError] = useState<string | null>(
    null,
  );
  const [branches, setBranches] = useState<SourceControlBranchOption[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState<string | null>(null);
  const [manualBranchEntry, setManualBranchEntry] = useState(false);
  // Branch-create (PR-A). In `create` mode `branch` is the BASE branch and
  // `newBranch` the name to create; the permission is pinned to write.
  const [branchMode, setBranchMode] = useState<BranchMode>("existing");
  const [newBranch, setNewBranch] = useState("");
  const [collisionPolicy, setCollisionPolicy] =
    useState<CollisionPolicy>("fail");
  // One immutable intent ⇒ one idempotency key. Any change to what would be
  // created (repository, name, base, collision policy) is a new intent; an
  // unchanged retry (and the follower re-POST) reuses the same key.
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );
  useEffect(() => {
    setIdempotencyKey(crypto.randomUUID());
  }, [branch, collisionPolicy, newBranch, repositoryId]);

  // Auto-select when there is exactly one provider — skip a pointless step.
  useEffect(() => {
    if (!providerId && catalog.providers.length === 1) {
      setProviderId(catalog.providers[0].providerId);
    }
  }, [catalog.providers, providerId]);

  const { loadBranches, loadRepositories } = catalog;
  const setUserBranch = (value: string) => {
    branchTouchedRef.current = true;
    setBranch(value);
  };

  useEffect(() => {
    if (!providerId) {
      setRepositories([]);
      setRepositoryId(null);
      branchTouchedRef.current = false;
      setBranch("");
      setBranches([]);
      setBranchesError(null);
      setBranchesLoading(false);
      setManualBranchEntry(false);
      return undefined;
    }
    let cancelled = false;
    setRepositoriesLoading(true);
    setRepositoriesError(null);
    setRepositoryId(null);
    branchTouchedRef.current = false;
    setBranch("");
    setBranches([]);
    setBranchesError(null);
    setBranchesLoading(false);
    setManualBranchEntry(false);
    void loadRepositories(providerId)
      .then((list) => {
        if (!cancelled) {
          setRepositories(list);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setRepositoriesError(
            error instanceof Error
              ? error.message
              : "Failed to load repositories",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setRepositoriesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadRepositories, providerId]);

  const selectedRepository =
    repositories.find(
      (repository) => repository.repositoryId === repositoryId,
    ) ?? null;

  useEffect(() => {
    if (!providerId || !selectedRepository) {
      setBranches([]);
      setBranchesError(null);
      setBranchesLoading(false);
      setManualBranchEntry(false);
      branchTouchedRef.current = false;
      setBranch("");
      return undefined;
    }

    let cancelled = false;
    setBranches([]);
    setBranchesError(null);
    setBranchesLoading(true);
    setManualBranchEntry(false);
    branchTouchedRef.current = false;
    setBranch(selectedRepository.defaultBranch ?? "");

    void loadBranches(providerId, selectedRepository.remoteSlug)
      .then((list) => {
        if (!cancelled) {
          setBranches(list);
          setBranch((currentBranch) =>
            resolveAutoBranchSelection({
              branchTouched: branchTouchedRef.current,
              branches: list,
              catalogDefaultBranch: selectedRepository.defaultBranch,
              currentBranch,
            }),
          );
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setBranches([]);
          setBranchesError(
            error instanceof Error ? error.message : "Failed to load branches",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBranchesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadBranches, providerId, selectedRepository]);

  const providerItems = catalog.providers.map((provider) => ({
    label: provider.displayName,
    meta: provider.type,
    value: provider.providerId,
  }));
  const repositoryItems = repositories.map((repository) => {
    const alreadyAttached = providerId
      ? attachedRepoKeys.has(`${providerId}:${repository.repositoryId}`)
      : false;
    return {
      badge: alreadyAttached ? "attached" : null,
      description: repository.defaultBranch
        ? `default · ${repository.defaultBranch}`
        : null,
      disabled: alreadyAttached,
      label: repository.remoteSlug,
      value: repository.repositoryId,
    };
  });
  const branchItems = branches.map((branchOption) => ({
    badge: branchOption.isDefault ? "default" : null,
    label: branchOption.name,
    value: branchOption.name,
  }));
  const showBranchList =
    !manualBranchEntry && !branchesError && branchItems.length > 0;
  const showBranchInput =
    manualBranchEntry ||
    Boolean(branchesError) ||
    (!branchesLoading && branchItems.length === 0);

  const isCreate = branchMode === "create";
  const trimmedNewBranch = newBranch.trim();
  const newBranchValid =
    trimmedNewBranch.length > 0 && isCreatableGitBranchName(trimmedNewBranch);
  const newBranchExists = branches.some(
    (branchOption) => branchOption.name === trimmedNewBranch,
  );
  const baseBranch = branch.trim();
  const createReady = newBranchValid && baseBranch.length > 0;
  const canSubmit =
    Boolean(providerId && repositoryId) && !busy && (!isCreate || createReady);

  const collision =
    errorCause instanceof RepositoryAttachError &&
    (errorCause.code === "branch_already_exists" ||
      errorCause.code === "branch_outcome_unknown")
      ? errorCause
      : null;
  const existingBranch =
    collision &&
    typeof collision.details.existingBranch === "string" &&
    collision.details.existingBranch.length > 0
      ? collision.details.existingBranch
      : trimmedNewBranch;

  const switchMode = (mode: BranchMode) => {
    setBranchMode(mode);
    setCollisionPolicy("fail");
    if (mode === "create") {
      setPermission("write");
    }
  };

  const submit = (policy: CollisionPolicy = collisionPolicy) => {
    if (!providerId || !repositoryId) {
      return;
    }
    if (!isCreate) {
      onSubmit({
        branchSelection: {
          branch: baseBranch.length > 0 ? baseBranch : null,
          mode: "existing",
        },
        permission,
        providerId,
        repositoryId,
      });
      return;
    }
    onSubmit({
      branchSelection: {
        baseBranch,
        branch: trimmedNewBranch,
        collisionPolicy: policy,
        idempotencyKey,
        mode: "create",
      },
      permission: "write",
      providerId,
      repositoryId,
    });
  };

  // An explicit post-collision choice is a NEW intent: never silently reuse
  // or suffix under the failed key.
  const resolveCollision = (policy: Exclude<CollisionPolicy, "fail">) => {
    const key = crypto.randomUUID();
    setCollisionPolicy(policy);
    setIdempotencyKey(key);
    if (!providerId || !repositoryId) {
      return;
    }
    onSubmit({
      branchSelection: {
        baseBranch,
        branch: trimmedNewBranch,
        collisionPolicy: policy,
        idempotencyKey: key,
        mode: "create",
      },
      permission: "write",
      providerId,
      repositoryId,
    });
  };

  return (
    <Modal
      title="Attach Repository"
      subtitle="Clone a repository into this workspace for actors to work in"
      widthClass="max-w-2xl"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={onBatch}
          >
            Attach multiple repositories
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            loading={busy}
            loadingText={
              progressMessage ?? (isCreate ? "Creating…" : "Attaching…")
            }
            onClick={() => submit()}
          >
            {isCreate ? "Create & attach" : "Attach"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-2.5">
        {collision ? (
          <CollisionChoices
            busy={busy}
            existingBranch={existingBranch}
            message={collision.message}
            outcomeUnknown={collision.code === "branch_outcome_unknown"}
            onAttachExisting={() => resolveCollision("reuse_existing")}
            onCreateSuffixed={() => resolveCollision("suffix")}
          />
        ) : (
          errorMessage && <ModalErrorBlock message={errorMessage} />
        )}
        {catalog.error && <ModalErrorBlock message={catalog.error} />}

        {catalog.providers.length > 1 && (
          <SearchableOptionList
            dense
            label="Provider"
            items={providerItems}
            selectedValue={providerId}
            onSelect={setProviderId}
            searchPlaceholder="Search providers…"
            emptyMessage={
              catalog.loading ? "Loading providers…" : "No providers configured"
            }
          />
        )}

        {providerId && (
          <div className="grid gap-2">
            <SearchableOptionList
              dense
              label="Repository"
              items={repositoryItems}
              selectedValue={repositoryId}
              onSelect={setRepositoryId}
              searchPlaceholder="Search repositories…"
              emptyMessage={
                repositoriesLoading
                  ? "Loading repositories…"
                  : "No repositories in this provider"
              }
            />
            {repositoriesError && (
              <ModalErrorBlock message={repositoriesError} />
            )}
          </div>
        )}

        {repositoryId && (
          <>
            <BranchModeSelector value={branchMode} onChange={switchMode} />
            {isCreate && (
              <label className={FIELD_LABEL_CLASS}>
                New branch name
                <input
                  value={newBranch}
                  placeholder="feature/VAUL-1234-short-slug"
                  autoFocus
                  onChange={(event) => setNewBranch(event.currentTarget.value)}
                  className={FIELD_INPUT_CLASS}
                />
                {trimmedNewBranch.length > 0 && !newBranchValid && (
                  <span className="text-[10px] normal-case tracking-normal text-amber-700 dark:text-amber-300">
                    Use ASCII letters, digits, `.`, `_`, `-` and `/` (max 128
                    chars); no leading `/`, trailing `.lock`, or `..`.
                  </span>
                )}
                {newBranchValid && newBranchExists && (
                  <span className="text-[10px] normal-case tracking-normal text-amber-700 dark:text-amber-300">
                    A branch with this name already exists on the remote — the
                    create will be refused and you can choose what to do.
                  </span>
                )}
              </label>
            )}
            <PermissionSelector
              value={permission}
              onChange={setPermission}
              lockedReason={
                isCreate
                  ? "Creating a branch requires write — the new branch is the only branch that gets push enabled automatically."
                  : null
              }
            />
            <div className="grid gap-2">
              {branchesLoading && (
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Loading branches…
                </p>
              )}
              {showBranchList && (
                <SearchableOptionList
                  dense
                  label={isCreate ? "Base branch" : "Branch (optional)"}
                  items={branchItems}
                  selectedValue={branch || null}
                  onSelect={setUserBranch}
                  searchPlaceholder="Search branches…"
                  emptyMessage="No matching branches"
                  maxHeightClassName="max-h-40"
                />
              )}
              {showBranchInput && (
                <label className={FIELD_LABEL_CLASS}>
                  {isCreate ? "Base branch" : "Branch (optional)"}
                  <input
                    value={branch}
                    placeholder={
                      selectedRepository?.defaultBranch ?? "default branch"
                    }
                    onChange={(event) =>
                      setUserBranch(event.currentTarget.value)
                    }
                    className={FIELD_INPUT_CLASS}
                  />
                </label>
              )}
              {branchesError && (
                <p className="text-[10px] leading-snug text-slate-500 dark:text-slate-400">
                  Couldn't list branches — type one manually.
                </p>
              )}
              {!branchesError &&
                !branchesLoading &&
                branchItems.length === 0 && (
                  <p className="text-[10px] leading-snug text-slate-500 dark:text-slate-400">
                    No branches were returned — type one manually.
                  </p>
                )}
              {showBranchList && (
                <button
                  type="button"
                  onClick={() => {
                    branchTouchedRef.current = true;
                    setManualBranchEntry(true);
                  }}
                  className={LINK_BUTTON_CLASS}
                >
                  Type branch manually
                </button>
              )}
              {manualBranchEntry &&
                branchItems.length > 0 &&
                !branchesError && (
                  <button
                    type="button"
                    onClick={() => setManualBranchEntry(false)}
                    className={LINK_BUTTON_CLASS}
                  >
                    Use branch list
                  </button>
                )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export function resolveAutoBranchSelection(input: {
  branchTouched: boolean;
  branches: SourceControlBranchOption[];
  catalogDefaultBranch: string | null;
  currentBranch: string;
}): string {
  if (input.branchTouched) {
    return input.currentBranch;
  }
  return (
    input.branches.find((branchOption) => branchOption.isDefault)?.name ??
    input.branches[0]?.name ??
    input.catalogDefaultBranch ??
    ""
  );
}

function BranchModeSelector({
  onChange,
  value,
}: {
  onChange: (value: BranchMode) => void;
  value: BranchMode;
}) {
  return (
    <div className="grid gap-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
        Branch
      </span>
      <div className="flex gap-2">
        {(["existing", "create"] as const).map((option) => {
          const active = value === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={`flex-1 border px-2 py-1 text-left font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${
                active
                  ? "border-sky-400 bg-sky-400/10 text-sky-700 dark:text-sky-300"
                  : "border-slate-300 text-slate-500 dark:border-slate-700 dark:text-slate-400"
              }`}
            >
              {option === "create" ? "Create new branch" : "Existing branch"}
              <span className="mt-0.5 block text-[8px] font-normal leading-snug tracking-[0.08em] text-slate-500">
                {option === "create"
                  ? "created once on the remote from a base branch; push enabled for it"
                  : "check out a branch that already exists"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * C3: a collision is never resolved silently. The user picks — attach the
 * existing branch (no automatic push authority) or create a suffixed name —
 * and each choice is a new immutable intent.
 */
function CollisionChoices({
  busy,
  existingBranch,
  message,
  onAttachExisting,
  onCreateSuffixed,
  outcomeUnknown,
}: {
  busy: boolean;
  existingBranch: string;
  message: string;
  onAttachExisting: () => void;
  onCreateSuffixed: () => void;
  outcomeUnknown: boolean;
}) {
  return (
    <div className="grid gap-2 border border-amber-400/60 bg-amber-400/10 px-3 py-2">
      <p className="text-xs font-bold text-amber-800 dark:text-amber-200">
        {outcomeUnknown
          ? `We could not confirm whether ${existingBranch} was created.`
          : `Branch ${existingBranch} already exists.`}
      </p>
      <p className="text-[11px] leading-snug text-amber-800/90 dark:text-amber-200/90">
        {message}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={onAttachExisting}
        >
          Attach existing branch
        </Button>
        <Button size="sm" disabled={busy} onClick={onCreateSuffixed}>
          Create with a suffix
        </Button>
      </div>
      <p className="text-[10px] leading-snug text-amber-800/80 dark:text-amber-200/80">
        Attaching the existing branch does not enable push for it; you can turn
        push on from the repository row afterwards.
      </p>
    </div>
  );
}

function PermissionSelector({
  lockedReason = null,
  onChange,
  value,
}: {
  lockedReason?: string | null;
  onChange: (value: WorkspaceRepositoryPermission) => void;
  value: WorkspaceRepositoryPermission;
}) {
  return (
    <div className="grid gap-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
        Permission
      </span>
      <div className="flex gap-2">
        {(["read", "write"] as const).map((option) => {
          const active = value === option;
          const locked = lockedReason !== null && option !== "write";
          const activeClass =
            option === "write"
              ? "border-amber-400 bg-amber-400/10 text-amber-700 dark:text-amber-300"
              : "border-emerald-400 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300";
          return (
            <button
              key={option}
              type="button"
              disabled={locked}
              onClick={() => onChange(option)}
              className={`flex-1 border px-2 py-1 text-left font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${
                active
                  ? activeClass
                  : "border-slate-300 text-slate-500 dark:border-slate-700 dark:text-slate-400"
              } ${locked ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {option}
              <span className="mt-0.5 block text-[8px] font-normal leading-snug tracking-[0.08em] text-slate-500">
                {option === "write"
                  ? "clone + pull + pushChanges (host broker); no sandbox push credentials"
                  : "clone + pull only"}
              </span>
            </button>
          );
        })}
      </div>
      {lockedReason && (
        <p className="text-[10px] leading-snug text-slate-500 dark:text-slate-400">
          {lockedReason}
        </p>
      )}
    </div>
  );
}
